import { type ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils/cn';

// Aspecto de stack de cartas: dos capas del mismo tamaño giradas por detrás,
// para que asomen las esquinas como en un mazo. Son decoración: aria-hidden.
const LAYER =
  'absolute inset-0 rounded-xl border border-white/10 bg-surface transition-transform duration-200';

export function Deck(): ReactNode {
  return (
    <>
      <span
        aria-hidden
        className={cn(LAYER, 'rotate-6 group-hover:rotate-[10deg]')}
      />
      <span
        aria-hidden
        className={cn(LAYER, 'rotate-3 group-hover:rotate-[5deg]')}
      />
    </>
  );
}

// Sin dato no inventamos un 0: esqueleto mientras carga y una raya si la
// petición falla. Un 0 falso se leería como "ese rol está vacío".
export function Count({
  count,
  failed,
}: {
  count: number | undefined;
  failed: boolean;
}): ReactNode {
  if (failed)
    return <span className="text-2xl text-text-tertiary sm:text-4xl">—</span>;
  if (count === undefined)
    return <Skeleton className="h-7 w-10 sm:h-10 sm:w-14" />;
  return (
    <span className="font-display text-2xl font-semibold tabular-nums text-text-primary sm:text-4xl">
      {count}
    </span>
  );
}

// Hueco de altura fija para el pie de la carta. Sin él, un tagline de dos
// líneas empuja el número hacia arriba y las tres cards dejan de alinear.
const SLOT = 'flex min-h-[1.1rem] items-start justify-center sm:min-h-[1.9rem]';

// El tagline no cabe en móvil: tres cards por fila dejan ~90px de ancho.
// El sello de bloqueado sí, que es corto y es la información que importa.
export function Tagline({
  tagline,
  locked,
}: {
  tagline: string | undefined;
  locked: boolean;
}): ReactNode {
  return (
    <span className={SLOT}>
      {locked ? (
        <span className="text-[9px] font-semibold text-brand-red sm:text-[11px]">
          Próximamente
        </span>
      ) : (
        <span className="hidden text-[11px] leading-snug text-text-tertiary sm:block">
          {tagline}
        </span>
      )}
    </span>
  );
}
